import type {
  Division,
  Pack,
  DeckUnit,
  GroupedPacksByCategory,
  GroupedDeckUnits,
  GroupedDeckUnitsByDescriptor,
  SlotCosts,
  PackMap,
  UnitMap,
} from "../types/deck-builder";
import { UnitCategory } from "../types/deck-builder";
import type { Unit } from "../types/unit";
import { InfoPanelType } from "../types/unit";
import type {
  SimpleDeck,
  SimpleUnitCard,
} from "@izohek/warno-deck-utils";
import {
  decodeDeckString,
  encodeDeck,
} from "@izohek/warno-deck-utils";
import { LookupAdapter } from "./LookupAdapter";
import { canonicalizeFactoryDescriptor } from "../utils/factory-descriptor";

export interface DeckConstructorOptions {
  division: Division;
  unitMap: UnitMap;
}

export interface DeckStringOptions {
  unitMap: UnitMap;
  divisions: Division[];
}

export type DeckChangeCallback = () => void;

export class Deck {
  private _units: DeckUnit[] = [];
  private _changeCallbacks: DeckChangeCallback[] = [];
  private _groupedAvailableUnits: GroupedPacksByCategory;
  private _unitsInDeckGroupedUnitsByCategory: GroupedDeckUnits;
  private _unitInDeckGroupedUnitsByPackDescriptor: GroupedDeckUnitsByDescriptor;
  private _unitsInDeckGroupedByDescriptor: { [key: string]: DeckUnit[] } = {};
  private _maxAvailabilityOfUnitCard: { [key: string]: number } = {};

  division: Division;
  unitMap: UnitMap;
  packMap: PackMap;
  slotCosts: SlotCosts;

  constructor(options: DeckConstructorOptions) {
    this.division = options.division;
    this.unitMap = options.unitMap;

    this._groupedAvailableUnits = this._groupAvailableUnits(this.division);

    const packMap: PackMap = {};
    for (const pack of this.division.packs) {
      packMap[pack.packDescriptor] = pack;
    }

    this.packMap = packMap;

    const slotCosts: SlotCosts = {
      [UnitCategory.LOG]: [],
      [UnitCategory.INF]: [],
      [UnitCategory.ART]: [],
      [UnitCategory.TNK]: [],
      [UnitCategory.REC]: [],
      [UnitCategory.AA]: [],
      [UnitCategory.HEL]: [],
      [UnitCategory.AIR]: [],
      [UnitCategory.DEFENSE]: [],
    };

    for (const matrixRow of this.division.costMatrix.matrix) {
      const categoryName = canonicalizeFactoryDescriptor(matrixRow.name) as UnitCategory;
      if (slotCosts[categoryName]) {
        slotCosts[categoryName] = matrixRow.activationCosts;
      }
    }

    this.slotCosts = slotCosts;

    for (const pack of this.division.packs) {
      const unitDescriptor = pack.unitDescriptor;
      const availability = pack.numberOfCards;
      this._maxAvailabilityOfUnitCard[unitDescriptor] = availability;
    }

    // Initialize grouped units
    this._unitsInDeckGroupedUnitsByCategory = this._groupUnitsByDeckCategory([]);
    this._unitInDeckGroupedUnitsByPackDescriptor = {};
  }

  public get units(): DeckUnit[] {
    return this._units;
  }

  public set units(value: DeckUnit[]) {
    this._units = value;
    this._unitsInDeckGroupedUnitsByCategory = this._groupUnitsByDeckCategory(value);
    this._unitInDeckGroupedUnitsByPackDescriptor = this._groupUnitsByPackDescriptor(value);

    const unitsGroupedUnitsByDescriptor: { [key: string]: DeckUnit[] } = {};

    for (const unit of this._units) {
      const descriptor = unit.pack.unitDescriptor;
      if (!unitsGroupedUnitsByDescriptor[descriptor]) {
        unitsGroupedUnitsByDescriptor[descriptor] = [];
      }
      unitsGroupedUnitsByDescriptor[descriptor]!.push(unit);
    }

    this._unitsInDeckGroupedByDescriptor = unitsGroupedUnitsByDescriptor;
    this.deckChanged();
  }

  public get unitsInDeckGroupedUnitsByCategory(): GroupedDeckUnits {
    return this._unitsInDeckGroupedUnitsByCategory;
  }

  public get unitsInDeckGroupedUnitsByDescriptor(): GroupedDeckUnitsByDescriptor {
    return this._unitInDeckGroupedUnitsByPackDescriptor;
  }

  public get availableUnits(): GroupedPacksByCategory {
    return this._groupedAvailableUnits;
  }

  public get totalSpentActivationPoints(): number {
    let totalActivationPoints = 0;

    for (const _category in this.slotCosts) {
      const category: UnitCategory = _category as UnitCategory;
      const slotCosts = this.slotCosts[category];
      const unitsInDeckCategory = this.unitsInDeckGroupedUnitsByCategory[category];

      if (unitsInDeckCategory && slotCosts) {
        const numberOfUnitsInCategory = unitsInDeckCategory.length;

        for (let i = 0; i < numberOfUnitsInCategory && i < slotCosts.length; i++) {
          totalActivationPoints += slotCosts[i] || 0;
        }
      }
    }

    return totalActivationPoints;
  }

  private _groupUnitsByDeckCategory(units: DeckUnit[]): GroupedDeckUnits {
    const groupedUnits: GroupedDeckUnits = {
      [UnitCategory.LOG]: [],
      [UnitCategory.INF]: [],
      [UnitCategory.ART]: [],
      [UnitCategory.TNK]: [],
      [UnitCategory.REC]: [],
      [UnitCategory.AA]: [],
      [UnitCategory.HEL]: [],
      [UnitCategory.AIR]: [],
      [UnitCategory.DEFENSE]: [],
    };

    for (const deckUnit of units) {
      if (deckUnit) {
        const categoryDescriptor = this.getDeckCategoryForPack(deckUnit.pack);

        if (categoryDescriptor !== undefined) {
          groupedUnits[categoryDescriptor].push(deckUnit);
        }
      }
    }

    return groupedUnits;
  }

  private _groupUnitsByPackDescriptor(units: DeckUnit[]): GroupedDeckUnitsByDescriptor {
    const groupedUnits: GroupedDeckUnitsByDescriptor = {};

    for (const deckUnit of units) {
      if (deckUnit) {
        const descriptor = deckUnit.pack.packDescriptor;
        if (groupedUnits[descriptor] === undefined) {
          groupedUnits[descriptor] = [];
        }
        groupedUnits[descriptor]!.push(deckUnit);
      }
    }

    return groupedUnits;
  }

  private _groupAvailableUnits(division: Division): GroupedPacksByCategory {
    const groupedPacksByCategoryByUnitCategory: GroupedPacksByCategory = {
      [UnitCategory.LOG]: {},
      [UnitCategory.INF]: {},
      [UnitCategory.ART]: {},
      [UnitCategory.TNK]: {},
      [UnitCategory.REC]: {},
      [UnitCategory.AA]: {},
      [UnitCategory.HEL]: {},
      [UnitCategory.AIR]: {},
      [UnitCategory.DEFENSE]: {},
    };

    for (const pack of division.packs) {
      const categoryDescriptor = this.getDeckCategoryForPack(pack) || UnitCategory.LOG;
      const unitSpeciality = this.getSpecialityForPack(pack);

      if (groupedPacksByCategoryByUnitCategory[categoryDescriptor][unitSpeciality] === undefined) {
        groupedPacksByCategoryByUnitCategory[categoryDescriptor][unitSpeciality] = [];
      }

      groupedPacksByCategoryByUnitCategory[categoryDescriptor][unitSpeciality].push(pack);
    }

    for (const category in groupedPacksByCategoryByUnitCategory) {
      const group = groupedPacksByCategoryByUnitCategory[category as UnitCategory];
      if (group) {
        for (const speciality in group) {
          const packs = group[speciality];
          if (packs) {
            // sort by command points
            group[speciality] = packs.sort((a, b) => {
              const unitA = this.getUnitForPack(a);
              const unitB = this.getUnitForPack(b);
              return (unitA?.commandPoints || 0) - (unitB?.commandPoints || 0);
            });
          }
        }
      }
    }

    return groupedPacksByCategoryByUnitCategory;
  }

  public addUnit(deckUnit: DeckUnit, skipValidation: boolean = false) {
    if (!skipValidation) {
      const availableQuantityOfPack = this.getAvailableQuantityOfPack(deckUnit.pack);

      if (availableQuantityOfPack === 0) {
        return;
      }

      const unitCategory = this.getDeckCategoryForPack(deckUnit.pack);

      if (unitCategory) {
        const nextSlotCost = this.getNextSlotCostForCategory(unitCategory);

        if (nextSlotCost === undefined) {
          return;
        }

        const nextTotalCost = this.usedActivationPoints + nextSlotCost;

        if (nextTotalCost > this.division.maxActivationPoints) {
          return;
        }
      }
    }

    const unitCategory = this.getDeckCategoryForPack(deckUnit.pack);
    if (unitCategory) {
      this.units = [...this._units, deckUnit];
    }
  }

  public removeUnit(unit: DeckUnit) {
    const deckWithoutUnit = this.units.filter((_unit) => unit !== _unit);
    this.units = [...deckWithoutUnit];
  }

  public clearDeck() {
    this.units = [];
  }

  public get unitCategories(): UnitCategory[] {
    return [
      UnitCategory.LOG,
      UnitCategory.INF,
      UnitCategory.ART,
      UnitCategory.TNK,
      UnitCategory.REC,
      UnitCategory.AA,
      UnitCategory.HEL,
      UnitCategory.AIR,
    ];
  }

  public getUnitForDescriptor(descriptor: string): Unit | undefined {
    return this.unitMap?.[descriptor];
  }

  public getUnitForPack(pack: Pack): Unit | undefined {
    const unitDescriptor = pack.unitDescriptor;
    return this.getUnitForDescriptor(unitDescriptor);
  }

  public getPackForUnitDescriptor(descriptor: string): Pack | undefined {
    return this.division.packs.find((pack) => pack.unitDescriptor === descriptor);
  }

  public getTransportsForPack(pack: Pack): Unit[] | undefined {
    if (pack.availableTransportList && pack.availableTransportList.length > 0) {
      return pack.availableTransportList
        .map((transportDescriptor) => this.getUnitForDescriptor(transportDescriptor))
        .filter((unit): unit is Unit => unit !== undefined);
    }

    return undefined;
  }

  public getPackAvailableWithoutTransport(pack: Pack): boolean {
    return pack.availableWithoutTransport || false;
  }

  public static getDefaultVeterancyForPack(pack: Pack, unit?: Unit): number {
    const quantities = Deck.getVeterancyQuantitiesForPack(pack, unit);
    const idx = pack.numberOfUnitInPackXPMultiplier.findIndex((multiplier) => multiplier === 1);
    if (idx >= 0 && quantities[idx] !== undefined && quantities[idx]! > 0) {
      return idx;
    }
    const first = quantities.findIndex((q) => q > 0);
    return first >= 0 ? first : 0;
  }

  private static isSupplyUnit(unit: Unit | undefined): boolean {
    if (!unit) {
      return false;
    }
    return (
      unit.infoPanelType === InfoPanelType.SUPPLY_VEHICLE ||
      unit.infoPanelType === InfoPanelType.SUPPLY_HELICOPTER
    );
  }

  public static getVeterancyQuantitiesForPack(pack: Pack, unit?: Unit): number[] {
    const quantities = Deck.convertMultipliersToQuantities(
      pack.numberOfUnitInPackXPMultiplier,
      pack.numberOfUnitsInPack
    );
    if (Deck.isSupplyUnit(unit)) {
      return quantities.map((q, i) => (i === 0 ? q : 0));
    }
    return quantities;
  }

  public static convertMultipliersToQuantities(
    multipliers: number[],
    numberOfUnitsInPack: number
  ): number[] {
    return multipliers.map((quantity) => Math.round(quantity * numberOfUnitsInPack));
  }

  public getUnitQuantityForPack(pack: Pack, veterancy: number): number {
    const unit = this.getUnitForPack(pack);
    const veterancyQuantities = Deck.getVeterancyQuantitiesForPack(pack, unit);
    return veterancyQuantities[veterancy] || 0;
  }

  public getAvailableQuantityOfPack(pack: Pack): number {
    const numberOfUsedCards =
      this.unitsInDeckGroupedUnitsByDescriptor[pack.packDescriptor]?.length || 0;

    const maximumNumberOfCards = pack.numberOfCards;
    return maximumNumberOfCards - numberOfUsedCards;
  }

  public getAvailableQuantityOfUnit(unitDescriptor: string): number {
    const numberOfUsedCards = this._unitsInDeckGroupedByDescriptor[unitDescriptor]?.length || 0;
    const maximumNumberOfCards = this._maxAvailabilityOfUnitCard[unitDescriptor] || 0;
    return maximumNumberOfCards - numberOfUsedCards;
  }

  public getNextSlotCostForCategory(category: UnitCategory): number | undefined {
    const unitsInDeckCategory = this.unitsInDeckGroupedUnitsByCategory[category];
    const numberOfUnitsInCategory = unitsInDeckCategory.length;
    const slotCostsForCategory = this.slotCosts[category];
    if (!slotCostsForCategory || numberOfUnitsInCategory >= slotCostsForCategory.length) {
      return undefined;
    }
    return slotCostsForCategory[numberOfUnitsInCategory];
  }

  public getNextSlotCostIndexForCategory(category: UnitCategory): number | undefined {
    const unitsInDeckCategory = this.unitsInDeckGroupedUnitsByCategory[category];
    return unitsInDeckCategory.length;
  }

  public getTotalSlotsForCategory(category: UnitCategory): number {
    const slotCostsForCategory = this.slotCosts[category];
    return slotCostsForCategory.length;
  }

  public getTotalUnitCountForCategory(category: UnitCategory): number {
    const unitsInDeckCategory = this.unitsInDeckGroupedUnitsByCategory[category];
    let unitCount = 0;
    for (const deckUnit of unitsInDeckCategory) {
      unitCount += this.getUnitQuantityForPack(deckUnit.pack, deckUnit.veterancy);
    }
    return unitCount;
  }

  public getSumOfUnitCostsForCategory(category: UnitCategory): number {
    const unitsInDeckCategory = this.unitsInDeckGroupedUnitsByCategory[category];
    let totalCost = 0;

    for (const deckUnit of unitsInDeckCategory) {
      const transportCost = deckUnit.transport?.commandPoints || 0;
      const unitCost = this.getUnitForPack(deckUnit.pack)?.commandPoints || 0;
      const costPerCard = unitCost + transportCost;
      const veterancyQuantities = Deck.getVeterancyQuantitiesForPack(
        deckUnit.pack,
        this.getUnitForPack(deckUnit.pack)
      );
      const quantityInCard = veterancyQuantities[deckUnit.veterancy] || 0;
      totalCost += costPerCard * quantityInCard;
    }
    return totalCost;
  }

  public getSumOfUnitsCosts(): number {
    let totalSum = 0;
    for (const category in this.unitsInDeckGroupedUnitsByCategory) {
      const categoryEnum: UnitCategory = category as UnitCategory;
      totalSum += this.getSumOfUnitCostsForCategory(categoryEnum);
    }
    return totalSum;
  }

  public get usedActivationPoints(): number {
    let totalPoints = 0;
    for (const category in this.unitsInDeckGroupedUnitsByCategory) {
      const categoryEnum: UnitCategory = category as UnitCategory;
      const slotsCostsForCategory = this.slotCosts[categoryEnum];
      const unitsInDeckCategory = this.unitsInDeckGroupedUnitsByCategory[categoryEnum];
      const numberOfUnitsInDeckCategory = unitsInDeckCategory.length;

      const occupiedSlotCosts = slotsCostsForCategory.slice(0, numberOfUnitsInDeckCategory);
      const slotPointsOccupied = occupiedSlotCosts.reduce((partialSum, a) => partialSum + a, 0);
      totalPoints += slotPointsOccupied;
    }

    return totalPoints;
  }

  public getDeckCategoryForPack(pack: Pack): UnitCategory | undefined {
    const factoryDescriptor = canonicalizeFactoryDescriptor(this.getUnitForPack(pack)?.factoryDescriptor);
    if (Object.values(UnitCategory).includes(factoryDescriptor as UnitCategory)) {
      return factoryDescriptor as UnitCategory;
    }
    return undefined;
  }

  public getSpecialityForPack(pack: Pack): string {
    const unit = this.getUnitForPack(pack);
    return unit?.specialities?.[0] || "";
  }

  // React-friendly change notification
  public onChange(callback: DeckChangeCallback): () => void {
    this._changeCallbacks.push(callback);
    return () => {
      const index = this._changeCallbacks.indexOf(callback);
      if (index > -1) {
        this._changeCallbacks.splice(index, 1);
      }
    };
  }

  private deckChanged() {
    this._changeCallbacks.forEach((callback) => callback());
  }

  public toDeckCode(): string {
    const cards = this._units
      .map((unit) => {
        const unitCard = this.getUnitForPack(unit.pack);
        if (!unitCard || unitCard.id === undefined) {
          console.warn("[Deck.toDeckCode] Unit missing ID:", {
            packDescriptor: unit.pack.packDescriptor,
            unitDescriptor: unit.pack.unitDescriptor,
            unitName: unitCard?.name,
          });
          return null;
        }
        return {
          unit: {
            id: unitCard.id,
          },
          veterancy: unit.veterancy,
          transport: unit.transport && unit.transport.id !== undefined ? { id: unit.transport.id } : undefined,
        } as SimpleUnitCard;
      })
      .filter((item) => item !== null) as SimpleUnitCard[];

    const deckBuilder: SimpleDeck = {
      modded: false,
      division: {
        id: this.division.id,
      },
      numberCards: cards.length,
      cards,
    };

    return encodeDeck(deckBuilder);
  }

  /**
   * Create a new Deck object from a deck code
   *
   * @param deckString The encoded deck code string
   * @param options Options containing unitMap and divisions
   * @returns A new Deck instance with the imported units
   */
  public static fromDeckCode(deckString: string, options: DeckStringOptions): Deck {
    const lookupAdapter = new LookupAdapter(options.unitMap, options.divisions);
    let deckStringDeck;
    try {
      deckStringDeck = decodeDeckString(deckString, lookupAdapter);
    } catch (error) {
      console.error("[Deck.fromDeckCode] Failed to decode deck string:", error);
      throw new Error(`Failed to decode deck code: ${error instanceof Error ? error.message : "Unknown error"}`);
    }

    // Try to find division by descriptor first
    let decodedDivision = options.divisions?.find((d) => {
      return d.descriptor === deckStringDeck.division?.descriptor;
    });

    // If not found by descriptor, try to find by ID (fallback)
    if (!decodedDivision && deckStringDeck.division?.id !== undefined) {
      decodedDivision = options.divisions?.find((d) => {
        return d.id === deckStringDeck.division?.id;
      });
    }

    // We must have a division to continue
    if (!decodedDivision) {
      const availableDescriptors = options.divisions.map(d => `${d.descriptor} (id: ${d.id})`).join(", ");
      throw new Error(`Division not found. Decoded division descriptor: "${deckStringDeck.division?.descriptor}", ID: ${deckStringDeck.division?.id}. Available divisions: ${options.divisions.length}`);
    }

    const builtDeck = new Deck({
      unitMap: options.unitMap,
      division: decodedDivision,
    });

    // Convert decoded cards into this objects internal structure
    for (const card of deckStringDeck.cards) {
      if (!card.unit.descriptor) {
        console.warn("[Deck.fromDeckCode] Card missing unit descriptor:", card);
        continue;
      }
      
      const pack = builtDeck.division.packs.find((divisionPack) => {
        return divisionPack.unitDescriptor === card.unit.descriptor;
      });

      // If we can't find a pack, skip this card
      if (!pack) {
        console.warn("[Deck.fromDeckCode] Pack not found for unit descriptor:", {
          unitDescriptor: card.unit.descriptor,
          unitId: card.unit.id,
          veterancy: card.veterancy,
          availablePacks: builtDeck.division.packs.length,
          samplePackDescriptors: builtDeck.division.packs.slice(0, 3).map(p => p.unitDescriptor),
        });
        continue; // Changed from break to continue to process remaining cards
      }

      let transport: string | undefined = undefined;

      try {
        transport = pack.availableTransportList?.find((transportDescriptor) => {
          return (
            card.transport?.descriptor?.toLowerCase() ===
            transportDescriptor.toLowerCase()
          );
        });
      } catch (e) {
        console.error(e);
      }

      const transportUnit = transport ? options.unitMap[transport] : undefined;

      // If we have transports then availableWithoutTransport must be set to skip adding a transport
      if (!pack.availableWithoutTransport && !transportUnit) {
        console.warn("[Deck.fromDeckCode] Pack requires transport but none found, skipping:", {
          unitDescriptor: card.unit.descriptor,
          packDescriptor: pack.packDescriptor,
        });
        continue;
      }

      const deckUnit = {
        pack: pack,
        transport: transportUnit,
        veterancy: card.veterancy,
      };

      const unitsBeforeAdd = builtDeck.units.length;
      builtDeck.addUnit(deckUnit, true); // Skip validation during import
      if (builtDeck.units.length <= unitsBeforeAdd) {
        console.warn("[Deck.fromDeckCode] Failed to add unit (no category match):", {
          unitDescriptor: card.unit.descriptor,
          packDescriptor: pack.packDescriptor,
        });
      }
    }

    return builtDeck;
  }
}

